import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Lock, EyeOff, Database, Trash2, Globe, Server, Menu, X, Home, Sun, Moon } from 'lucide-react';
import { useTranslations } from '../i18n';
import { usePreferences } from '../contexts/PreferencesContext';
import { LanguagePickerModal } from '../components/ui/LanguagePickerModal';
import { SupportedLanguage } from '../lib/expenseTypeHelpers';
import { SEO } from '../components/ui/SEO';

export function PrivacyPage() {
    const { language: currentLang, t } = useTranslations();
    const { preferences: interfacePrefs, updatePreferences } = usePreferences();
    const [showMenu, setShowMenu] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);

    // Fallback if privacy is not translated yet in other languages
    const rawPrivacy = typeof (t.landing as any).privacy === 'object' ? (t.landing as any).privacy : {};
    
    const privacy = {
        title: rawPrivacy.title || "Privacy Policy",
        lastUpdated: rawPrivacy.lastUpdated || "Last updated: March 24, 2026",
        introduction: rawPrivacy.introduction || "At My Car Costs, we take your privacy seriously. This policy explains how we handle your data in accordance with modern privacy standards, including GDPR.",
        dataCollection: {
            title: rawPrivacy.dataCollection?.title || "Data Collection",
            description: rawPrivacy.dataCollection?.description || "We only collect the absolute minimum information required to provide our service.",
            email: rawPrivacy.dataCollection?.email || "Account Identity: We use your email address as your unique identifier and for authentication (e.g., magic links or Google login).",
            documents: rawPrivacy.dataCollection?.documents || "Document Storage: Files you upload are stored securely in encrypted cloud storage."
        },
        carData: {
            title: rawPrivacy.carData?.title || "Your Data Control",
            description: rawPrivacy.carData?.description || "Information you enter (expenses, mileage, parts) is stored for your personal use and analytics.",
            noValidation: rawPrivacy.carData?.noValidation || "We do not validate the car data you provide; it is your responsibility to ensure accuracy.",
            privateByDefault: rawPrivacy.carData?.privateByDefault || "By default, your data is private. Technical identifiers for files are non-sequential and not indexed by search engines.",
            rightToForgotten: rawPrivacy.carData?.rightToForgotten || "Right to be Forgotten: You can delete your account at any time, which immediately and permanently removes all your records and files."
        },
        thirdParty: {
            title: rawPrivacy.thirdParty?.title || "Third-party Services",
            description: rawPrivacy.thirdParty?.description || "We do not sell your personal data. When using Google Login, we only access your email address for identification purposes."
        },
        emailIngestion: {
            title: rawPrivacy.emailIngestion?.title || "Email Ingestion",
            description: rawPrivacy.emailIngestion?.description || "Emails sent to our ingestion service are stored to provide a processing history. You can manually delete individual emails or your entire history at any time."
        }
    };

    return (
        <div className="min-h-screen bg-transparent font-primary text-slate-900 dark:text-slate-100 flex flex-col">
            <SEO 
                title={`${privacy.title} | MyCarCosts`} 
                description="Our commitment to your data privacy and security at MyCarCosts."
            />
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowMenu(true)}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-blue-600 dark:text-blue-400"
                        >
                            <Menu size={20} />
                        </button>
                        <span className="text-xl font-bold tracking-tight">{privacy.title}</span>
                    </div>
                </div>
            </header>

            {/* Side Menu Drawer (Mirroring /repair view) */}
            {showMenu && (
                <div className="fixed inset-0 z-[100] flex items-start justify-start">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMenu(false)} />
                    <div className="relative w-[280px] h-full bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
                        <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                            <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">{t.common.menu}</h2>
                            <button onClick={() => setShowMenu(false)} className="text-gray-400 hover:text-red-500 transition-colors"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                            <Link
                                to="/"
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/40 text-blue-600 rounded-lg"><Home size={18} /></div>
                                <span className="text-sm font-bold">{t.common.home}</span>
                            </Link>
                            <button
                                onClick={() => { setShowLanguageModal(true); setShowMenu(false); }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 rounded-lg"><Globe size={18} /></div>
                                <span className="text-sm font-bold">{t.settings?.language || 'Language'}</span>
                            </button>
                            <button
                                onClick={() => { updatePreferences({ theme: interfacePrefs?.theme === 'light' ? 'dark' : 'light' }); setShowMenu(false); }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-amber-50 dark:bg-amber-900/40 text-amber-600 rounded-lg">
                                    {interfacePrefs?.theme === 'light' ? <Sun size={18} /> : <Moon size={18} />}
                                </div>
                                <span className="text-sm font-bold">{t.settings?.interfaceTheme || 'Design'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <LanguagePickerModal 
                isOpen={showLanguageModal}
                title={t.settings?.language || 'Interface Language'}
                currentLanguage={(interfacePrefs?.language as SupportedLanguage) || currentLang || 'en'}
                onClose={() => setShowLanguageModal(false)}
                onSelect={(lang: SupportedLanguage) => {
                    updatePreferences({ language: lang });
                    setShowLanguageModal(false);
                }}
            />

            <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                <div className="mb-12">
                    <h1 className="text-4xl font-black mb-4">{privacy.title}</h1>
                    <p className="text-slate-500 dark:text-slate-400 font-medium italic">
                        {privacy.lastUpdated}
                    </p>
                </div>

                <div className="space-y-12 pb-32">
                    <div className="grid md:grid-cols-2 gap-8">
                        <section id="data-collection" className="space-y-4 scroll-mt-24">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center">
                                <Shield size={24} />
                            </div>
                            <h2 className="text-2xl font-bold">{privacy.dataCollection.title}</h2>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                {privacy.dataCollection.description}
                            </p>
                            <div className="space-y-3">
                                <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-100 dark:border-slate-800 flex gap-3">
                                    <Mail className="text-blue-500 flex-shrink-0" size={18} />
                                    <span className="text-sm font-medium">{privacy.dataCollection.email}</span>
                                </div>
                                {privacy.dataCollection.documents && (
                                    <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-100 dark:border-slate-800 flex gap-3">
                                        <Database className="text-indigo-500 flex-shrink-0" size={18} />
                                        <span className="text-sm font-medium">{privacy.dataCollection.documents}</span>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section id="car-data" className="space-y-4 scroll-mt-24">
                            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-2xl flex items-center justify-center">
                                <Lock size={24} />
                            </div>
                            <h2 className="text-2xl font-bold">{privacy.carData.title}</h2>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                {privacy.carData.description}
                            </p>
                            <div className="space-y-3">
                                <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-100 dark:border-slate-800 flex gap-3">
                                    <EyeOff className="text-orange-500 flex-shrink-0" size={18} />
                                    <span className="text-sm font-medium">{privacy.carData.privateByDefault}</span>
                                </div>
                                {privacy.carData.rightToForgotten && (
                                    <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100/50 dark:border-red-900/30 flex gap-3">
                                        <Trash2 className="text-red-500 flex-shrink-0" size={18} />
                                        <span className="text-sm font-medium text-red-900 dark:text-red-200">{privacy.carData.rightToForgotten}</span>
                                    </div>
                                )}
                                <p className="text-sm text-slate-500 italic pl-4 border-l-2 border-slate-200 dark:border-slate-800">
                                    {privacy.carData.noValidation}
                                </p>
                            </div>
                        </section>

                        <section id="third-party" className="space-y-4 scroll-mt-24">
                            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center">
                                <Globe size={24} />
                            </div>
                            <h2 className="text-2xl font-bold">{privacy.thirdParty.title}</h2>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                {privacy.thirdParty.description}
                            </p>
                        </section>

                        <section id="email-ingestion" className="space-y-4 scroll-mt-24">
                            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center">
                                <Server size={24} />
                            </div>
                            <h2 className="text-2xl font-bold">{privacy.emailIngestion.title}</h2>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                {privacy.emailIngestion.description}
                            </p>
                        </section>
                    </div>
                </div>

                <div className="mt-20 pt-12 border-t border-slate-200 dark:border-slate-800 text-center">
                    <p className="text-slate-500 mb-6">
                        {t.landing.footer.questions}
                    </p>
                    <a
                        href={`mailto:${t.landing.footer.contactEmail}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                    >
                        <Mail size={18} />
                        {t.landing.footer.contactEmail}
                    </a>
                </div>
            </main>
        </div>
    );
}
